import React, { useState } from "react";

import {
  CheckCircle2,
  DownloadIcon,
  FileTextIcon,
  Loader2Icon,
  ShareIcon,
} from "lucide-react";
import {
  TenantReport,
  type TenantReportData,
  mapRealDataToComponentData,
} from "@multiburo/ui-components";
import { type UseMutationResult, useMutation } from "@tanstack/react-query";
import { toast } from "utils/toast";

import {
  downloadGuarantorMultireportExpress,
  downloadTenantMultireport,
  downloadTenantMultireportExpress,
  getTenantMultireportWebData,
} from "api/tenants";

import { useGenerateTenantMultireportPublicLink } from "hooks/useTenantReports";

import { Card, CardContent } from "js/components/ui/card";
import { Button } from "js/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "js/components/ui/dialog";
import { EmptyState } from "js/components/ui/empty-state";
import { Progress } from "js/components/ui/progress";

import PreviewPdf from "js/components/Contracts/PreviewPdf";
import DownloadItem from "js/components/Tenants/TenantReportDetails/DownloadItem";
import ReportSubBlock from "js/components/Tenants/TenantReportDetails/ReportSubBlock";
import ShareModal from "js/components/Tenants/TenantReportDetails/ShareModal";

import { CreditReportException } from "js/exceptions/CreditReportException";
import { decryptTenantReportData } from "js/utils/encryption";

interface DownloadableItem {
  name: string;
  status: string;
  type: string;
  documentPassword: string;
}

interface Props {
  downloadable: DownloadableItem[];
  tenant_report_id: string;
  tenantPhoneNumber?: string;
  landlordPhoneNumber?: string;
  /** Render as a collapsible sub-block (design 8a) instead of a standalone card. */
  asSubBlock?: boolean;
}

const REPORT_DESCRIPTIONS: Record<string, string> = {
  tenant_report: "Reporte completo del inquilino.",
  tenant_credit_history: "Reporte crediticio del inquilino.",
  guarantor_credit_history:
    "Reporte crediticio del fiador u obligado solidario.",
};

const STATUS_META: Record<
  string,
  { label: string; color: string; description: string; disabledHint: string }
> = {
  completed: {
    label: "Listo",
    color: "success",
    description: "",
    disabledHint: "",
  },
  pending: {
    label: "Pendiente",
    color: "warning",
    description: "",
    disabledHint:
      "Todavía no hay un archivo disponible. Revisa más tarde o actualiza la página.",
  },
  empty: {
    label: "Vacío",
    color: "warning",
    description: "",
    disabledHint:
      "El expediente está vacío. Sube documentos para poder descargarlo.",
  },
  default: {
    label: "Actualizando",
    color: "processing",
    description:
      "Estamos actualizando el estado del documento. Por favor intenta más tarde.",
    disabledHint: "El documento aún no está disponible",
  },
};

const normalizeStatusKey = (status?: string) => {
  if (!status) {
    return "pending";
  }

  const normalized = status.toLowerCase().replace(/\s+/g, "_");

  if (normalized === "created" || normalized === "pending_upload") {
    return "pending";
  }

  if (STATUS_META[normalized]) {
    return normalized;
  }

  return "pending";
};

const getStatusMeta = (status?: string) =>
  STATUS_META[normalizeStatusKey(status)];

const DownloadsDetailsComponent: React.FC<Props> = ({
  downloadable,
  tenant_report_id,
  tenantPhoneNumber,
  landlordPhoneNumber,
  asSubBlock = false,
}) => {
  // Blob cache state (for PDF preview)
  const [
    guarantorCreditHistoryMultireportPdfBlob,
    setGuarantorCreditHistoryMultireportPdfBlob,
  ] = useState<Blob | null>(null);
  const [
    tenantCreditHistoryMultireportPdfBlob,
    setTenantCreditHistoryMultireportPdfBlob,
  ] = useState<Blob | null>(null);
  const [tenantMultireportPdfBlob, setTenantMultireportPdfBlob] =
    useState<Blob | null>(null);

  // Modal visibility state
  const [
    isGuarantorCreditHistoryPdfModalVisible,
    setIsGuarantorCreditHistoryPdfModalVisible,
  ] = useState<boolean>(false);
  const [
    isTenantCreditHistoryPdfModalVisible,
    setIsTenantCreditHistoryPdfModalVisible,
  ] = useState<boolean>(false);
  const [isTenantPdfModalVisible, setIsTenantPdfModalVisible] =
    useState<boolean>(false);
  const [isTenantReportModalVisible, setIsTenantReportModalVisible] =
    useState<boolean>(false);
  const [isShareModalVisible, setIsShareModalVisible] =
    useState<boolean>(false);

  // Other state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [tenantReportData, setTenantReportData] =
    useState<TenantReportData | null>(null);
  const [shareReportLink, setShareReportLink] = useState<string>("");

  const handlePdfBlobError = async (error: any) => {
    let errorCode = "6050";

    if (
      error.response?.data instanceof Blob &&
      error.response.headers["content-type"]?.includes("application/json")
    ) {
      try {
        const text = await error.response.data.text();
        const errorData = JSON.parse(text);
        if (errorData.code) {
          errorCode = errorData.code;
        }
      } catch (parseError) {}
    } else if (error.response?.data?.code) {
      errorCode = error.response.data.code;
    }

    const exception = new CreditReportException(errorCode);
    toast.error(exception.message);
  };

  // TanStack Query Mutations

  // Fetch guarantor credit history PDF blob (for preview)
  const fetchGuarantorPdfMutation = useMutation({
    mutationFn: async (reportId: string) => {
      const response = await downloadGuarantorMultireportExpress(reportId);
      return new Blob([response.data], { type: "application/pdf" });
    },
    onSuccess: (blob) => {
      setGuarantorCreditHistoryMultireportPdfBlob(blob);
    },
    onError: handlePdfBlobError,
  });

  // Fetch tenant credit history PDF blob (for preview)
  const fetchTenantCreditHistoryPdfMutation = useMutation({
    mutationFn: async (reportId: string) => {
      const response = await downloadTenantMultireportExpress(reportId);
      return new Blob([response.data], { type: "application/pdf" });
    },
    onSuccess: (blob) => {
      setTenantCreditHistoryMultireportPdfBlob(blob);
    },
    onError: handlePdfBlobError,
  });

  const fetchTenantMultireportPdfMutation = useMutation({
    mutationFn: async (reportId: string) => {
      const response = await downloadTenantMultireport(reportId);
      return new Blob([response.data], { type: "application/pdf" });
    },
    onSuccess: (blob) => {
      setTenantMultireportPdfBlob(blob);
    },
    onError: () => {
      toast.error(
        "Error al descargar el reporte. Por favor intente nuevamente.",
      );
    },
  });

  const fetchTenantReportWebDataMutation = useMutation({
    mutationFn: async (reportId: string) => {
      const response = await getTenantMultireportWebData(reportId);

      let decryptedData;
      if (typeof response.data === "string") {
        const aesKey = import.meta.env.VITE_TENANT_REPORT_AES_KEY;
        decryptedData = decryptTenantReportData(response.data, aesKey);
      } else {
        decryptedData = response.data;
      }

      return mapRealDataToComponentData(decryptedData);
    },
    onSuccess: (transformedData) => {
      setTenantReportData(transformedData);
    },
    onError: () => {
      toast.error("Error al cargar el reporte. Por favor intente nuevamente.");
    },
  });

  // Generate or regenerate public share link
  const generateShareLinkMutation = useGenerateTenantMultireportPublicLink();

  // Derive loading states from mutations
  const isGuarantorCreditHistoryPdfLoading =
    fetchGuarantorPdfMutation.isPending;
  const isTenantCreditHistoryPdfLoading =
    fetchTenantCreditHistoryPdfMutation.isPending;
  const isTenantPdfLoading = fetchTenantMultireportPdfMutation.isPending;
  const isTenantReportLoading = fetchTenantReportWebDataMutation.isPending;
  const isGeneratingShareLink = generateShareLinkMutation.isPending;

  const handleShareClick = () => {
    setIsTenantReportModalVisible(false);
    generateShareLinkMutation.mutate(
      { reportId: tenant_report_id, regenerate: false },
      {
        onSuccess: (response) => {
          const baseUrl = window.location.origin;
          const link = `${baseUrl}/reports/multireport?tk=${response.token}`;
          setShareReportLink(link);
          setIsShareModalVisible(true);
        },
      },
    );
  };

  const handleRegenerateLink = () => {
    generateShareLinkMutation.mutate(
      { reportId: tenant_report_id, regenerate: true },
      {
        onSuccess: (response) => {
          const baseUrl = window.location.origin;
          const link = `${baseUrl}/reports/multireport?tk=${response.token}`;
          setShareReportLink(link);
        },
      },
    );
  };

  const handleItemPreview = (item: DownloadableItem) => {
    if (item.type == "guarantor_credit_history") {
      setIsGuarantorCreditHistoryPdfModalVisible(true);
      if (!guarantorCreditHistoryMultireportPdfBlob) {
        fetchGuarantorPdfMutation.mutate(tenant_report_id);
      }
    } else if (item.type == "tenant_credit_history") {
      setIsTenantCreditHistoryPdfModalVisible(true);
      if (!tenantCreditHistoryMultireportPdfBlob) {
        fetchTenantCreditHistoryPdfMutation.mutate(tenant_report_id);
      }
    } else if (
      item.type == "tenant_report" &&
      item.name === "Multireporte Inquilino"
    ) {
      // Open TenantReport component instead of PDF for Multireporte Inquilino
      setIsTenantReportModalVisible(true);
      if (!tenantReportData) {
        fetchTenantReportWebDataMutation.mutate(tenant_report_id);
      }
    } else {
      setIsTenantPdfModalVisible(true);
      if (!tenantMultireportPdfBlob) {
        fetchTenantMultireportPdfMutation.mutate(tenant_report_id);
      }
    }
  };

  const downloadPdf = (pdfBlob: Blob, fileName: string) => {
    if (!(pdfBlob instanceof Blob)) {
      toast.error("Error al descargar el archivo");
      return;
    }

    const pdfUrl = URL.createObjectURL(pdfBlob);
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(pdfUrl);
  };

  // Helper objects for mapping item types to states/mutations
  const blobPDFStates: Record<string, Blob | null> = {
    tenant_report: tenantMultireportPdfBlob,
    tenant_credit_history: tenantCreditHistoryMultireportPdfBlob,
    guarantor_credit_history: guarantorCreditHistoryMultireportPdfBlob,
  };

  const fetchPdfMutations: Record<
    string,
    UseMutationResult<Blob, unknown, string, unknown>
  > = {
    tenant_report: fetchTenantMultireportPdfMutation,
    tenant_credit_history: fetchTenantCreditHistoryPdfMutation,
    guarantor_credit_history: fetchGuarantorPdfMutation,
  };

  const loadingState: Record<string, boolean> = {
    tenant_report: isTenantPdfLoading,
    tenant_credit_history: isTenantCreditHistoryPdfLoading,
    guarantor_credit_history: isGuarantorCreditHistoryPdfLoading,
  };

  const setModalVisibleState: Record<string, boolean> = {
    tenant_report: isTenantPdfModalVisible,
    tenant_credit_history: isTenantCreditHistoryPdfModalVisible,
    guarantor_credit_history: isGuarantorCreditHistoryPdfModalVisible,
  };

  const hasDownloadable =
    Array.isArray(downloadable) && downloadable.length > 0;

  // Expediente ZIP download moved to the "Documentos del Expediente" section, so the
  // Descargas summary now counts only the report documents.
  const summaryItems: { status: string }[] = hasDownloadable
    ? downloadable.map((i) => ({ status: i?.status }))
    : [];
  const totalCount = summaryItems.length;
  const readyCount = summaryItems.filter(
    (i) => i?.status?.toLowerCase() === "completed",
  ).length;
  const progressValue = totalCount > 0 ? (readyCount / totalCount) * 100 : 0;

  const handleItemDownload = (item: DownloadableItem) => {
    const filename = item.name.replaceAll(" ", "_");
    const cachedBlob = blobPDFStates[item.type];

    // If blob is already cached, download immediately
    if (cachedBlob) {
      downloadPdf(cachedBlob, filename);
      return;
    }

    // Otherwise, fetch blob first, then download
    const fetchMutation = fetchPdfMutations[item.type];
    if (fetchMutation) {
      fetchMutation.mutate(tenant_report_id, {
        onSuccess: (blob) => {
          downloadPdf(blob, filename);
        },
      });
    }
  };

  const handleModalCancel = () => {
    setIsGuarantorCreditHistoryPdfModalVisible(false);
    setIsTenantCreditHistoryPdfModalVisible(false);
    setIsTenantPdfModalVisible(false);
    setIsTenantReportModalVisible(false);
    setCurrentPage(1);
  };

  const handleShareModalCancel = () => {
    setIsShareModalVisible(false);
  };

  const listosBadge = (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
      <CheckCircle2 className="size-3" />
      {readyCount} de {totalCount} listos
    </span>
  );

  const downloadsBody = (
    <div className="space-y-4">
      {/* Reportes section */}
      {hasDownloadable ? (
        <div>
          <div className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Reportes
          </div>
          <div className="overflow-hidden rounded-xl border bg-card divide-y divide-border">
            {downloadable.map((item, index) => {
                  const is_completed =
                    item?.status?.toLowerCase() === "completed";
                  const statusMeta = getStatusMeta(item?.status);
                  const reportDesc = REPORT_DESCRIPTIONS[item.type] ?? "";
                  const statusDescription = [reportDesc, statusMeta.description]
                    .filter(Boolean)
                    .join(" ");
                  const disabledHint = !is_completed
                    ? statusMeta.disabledHint
                    : undefined;
                  const isMultireporteInquilino =
                    item.type === "tenant_report" &&
                    item.name === "Multireporte Inquilino";

                  return (
                    <DownloadItem
                      key={`${item.type}-${index}`}
                      title={item?.name}
                      isCompleted={is_completed}
                      // Download always fetches the PDF blob (fetchPdfMutations[type]).
                      isDownloading={loadingState[item?.type]}
                      // View for Multireporte Inquilino opens the web report (a
                      // different mutation); every other item previews the same PDF.
                      isViewing={
                        isMultireporteInquilino
                          ? isTenantReportLoading
                          : loadingState[item?.type]
                      }
                      onDownload={() => handleItemDownload(item)}
                      onView={() => handleItemPreview(item)}
                      onShare={
                        item?.type === "tenant_report"
                          ? handleShareClick
                          : undefined
                      }
                      showShare={item?.type === "tenant_report"}
                      passwordText={item?.documentPassword || ""}
                      showPassword={!!item?.documentPassword}
                      statusLabel={statusMeta.label}
                      statusColor={statusMeta.color}
                      statusDescription={statusDescription}
                      disabledHint={disabledHint}
                      fileType="pdf"
                      primaryAction={
                        isMultireporteInquilino && is_completed
                          ? "view"
                          : "download"
                      }
                    />
                  );
                })}
              </div>
            </div>
          ) : (
            <EmptyState
              icon={FileTextIcon}
              title="Sin documentos disponibles"
              description="Los documentos se generarán automáticamente en cuanto estén disponibles."
            />
          )}
    </div>
  );

  return (
    <>
      {asSubBlock ? (
        <ReportSubBlock
          icon={DownloadIcon}
          title="Descargas"
          badge={listosBadge}
        >
          {downloadsBody}
        </ReportSubBlock>
      ) : (
        <Card className="gap-0">
          <CardContent className="space-y-4">
            <div>
              <h3 className="text-base font-semibold">Descargas</h3>
              <p className="text-sm text-muted-foreground">
                Documentos generados de este expediente
              </p>
            </div>
            <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2.5">
              <CheckCircle2 className="size-3.5 shrink-0 text-emerald-600" />
              <span className="text-xs font-medium text-foreground">
                {readyCount} de {totalCount} listos
              </span>
              <Progress
                value={progressValue}
                status="success"
                className="h-1 flex-1"
              />
            </div>
            {downloadsBody}
          </CardContent>
        </Card>
      )}

      {/* PDF Preview Dialogs */}
      {downloadable?.map((item, index) => {
        const isModalOpen = setModalVisibleState[item?.type];
        const modalLoading = loadingState[item?.type];
        const pdfBlob = blobPDFStates[item.type];
        const isCompleted = item?.status?.toLowerCase() === "completed";

        return (
          <Dialog
            key={`${item.type}-${index}`}
            open={isModalOpen}
            onOpenChange={(o) => !o && handleModalCancel()}
          >
            <DialogContent className="max-w-4xl sm:max-w-4xl p-0">
              <DialogHeader className="px-6 pt-6">
                <DialogTitle>{item?.name}</DialogTitle>
              </DialogHeader>

              {/* PDF Viewer */}
              <div style={{ height: "70vh", overflow: "auto" }}>
                {modalLoading ? (
                  <div className="flex h-full items-center justify-center">
                    <Loader2Icon className="size-12 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  pdfBlob && (
                    <PreviewPdf
                      pdfBlob={pdfBlob}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      password={item.documentPassword}
                    />
                  )
                )}
              </div>

              <DialogFooter className="px-6 pb-6">
                <Button variant="outline" onClick={handleModalCancel}>
                  Cerrar
                </Button>
                <Button
                  onClick={() => handleItemDownload(item)}
                  disabled={!isCompleted || modalLoading}
                >
                  {modalLoading ? (
                    <Loader2Icon className="size-4 animate-spin" />
                  ) : (
                    <DownloadIcon className="size-4" />
                  )}
                  Descargar PDF
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      })}

      {/* TenantReport Dialog for Multireporte Inquilino */}
      <Dialog
        open={isTenantReportModalVisible}
        onOpenChange={(o) => !o && handleModalCancel()}
      >
        <DialogContent className="max-w-[1200px] sm:max-w-[1200px] w-[90vw] p-0">
          <DialogHeader className="flex flex-row items-center justify-between px-6 pt-6">
            <DialogTitle>Multireporte Inquilino</DialogTitle>
            <Button
              size="sm"
              onClick={handleShareClick}
              disabled={isGeneratingShareLink}
              className="mr-8"
            >
              {isGeneratingShareLink ? (
                <Loader2Icon className="size-4 animate-spin" />
              ) : (
                <ShareIcon className="size-4" />
              )}
              Compartir
            </Button>
          </DialogHeader>

          <div style={{ height: "85vh", overflow: "auto" }}>
            {isTenantReportLoading ? (
              <div className="flex items-center justify-center" style={{ height: "80vh" }}>
                <Loader2Icon className="size-12 animate-spin text-muted-foreground" />
              </div>
            ) : (
              tenantReportData && (
                <TenantReport
                  data={tenantReportData}
                  onError={() => {
                    toast.error("Error al mostrar el reporte");
                  }}
                  onWebCheckUnlock={(_actorType, webCheckId) => {
                    if (webCheckId) {
                      window.open(`/pay/web-check/${webCheckId}`, "_blank");
                    }
                  }}
                  className="tenant-report-modal"
                />
              )
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <ShareModal
        open={isShareModalVisible}
        onCancel={handleShareModalCancel}
        reportLink={shareReportLink}
        tenantPhoneNumber={tenantPhoneNumber}
        landlordPhoneNumber={landlordPhoneNumber}
        onRegenerateLink={handleRegenerateLink}
        isRegenerating={isGeneratingShareLink}
      />
    </>
  );
};

export default DownloadsDetailsComponent;
